import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { advancedHostingActions } from '@/redux/modules/advancedHosting';
import useStatusFormatter from '@/hooks/useStatusFormatter';

/**
 * Hook que busca e retorna o status formatado de um servidor específico.
 * Cada servidor tem seu próprio estado no Redux, evitando sobrescrita.
 * Funciona tanto na listagem quanto na página de detalhes.
 *
 * @param {string|number} serverId - ID do servidor
 * @param {Object} options - Opções do hook
 * @param {boolean} options.autoFetch - Se deve buscar automaticamente (default: true)
 * @returns {{
 *   status: { value: string, variant: string } | null,
 *   statusData: string,
 *   loadingStatus: boolean,
 *   hasError: boolean,
 *   refetch: Function,
 * }}
 */
const useServerStatus = (serverId, options = {}) => {
  const { autoFetch = true } = options;
  const dispatch = useDispatch();
  const hasFetched = useRef({});

  // Busca o status específico deste servidor do estado mapeado
  const serverStatusState = useSelector(
    state => state.advancedHosting?.serverStatus?.byId?.[serverId],
  );

  const loadingStatus = serverStatusState?.loading ?? false;
  const statusData = serverStatusState?.status ?? null;
  const hasError = serverStatusState?.error ?? false;

  // Função para forçar refetch
  const refetch = () => {
    if (serverId) {
      hasFetched.current[serverId] = false;
      dispatch(advancedHostingActions.serverStatus.request({ serverId }));
    }
  };

  // Dispara a requisição apenas uma vez por serverId
  // Se houver erro, não tenta novamente até um refetch manual
  useEffect(() => {
    if (
      autoFetch
      && serverId
      && !hasFetched.current[serverId]
      && !loadingStatus
      && !statusData
      && !hasError
    ) {
      hasFetched.current[serverId] = true;
      dispatch(advancedHostingActions.serverStatus.request({ serverId }));
    }
  }, [dispatch, serverId, autoFetch, loadingStatus, statusData, hasError]);

  // Usa o hook de formatação
  const status = useStatusFormatter(statusData);

  return {
    status,
    statusData,
    loadingStatus,
    hasError,
    refetch,
  };
};

export default useServerStatus;
